import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { baseUrl } from 'constants/baseUrl';
import storageService from './storageService';

/**
 * Keycloak sign-in for TMR (HOLM-506).
 *
 * Authorization Code with PKCE (S256) through the public holedo-tmr client.
 *
 * The session (access, refresh and ID token) is kept in localStorage so every
 * tab of TMR shares it: a new tab is signed in and can renew. Refresh tokens
 * are single-use, so renewal is serialised across tabs with a Web Lock and
 * each tab first checks whether another tab has already renewed. The
 * short-lived access token is also copied into TMR's existing token slot so
 * the current API calls keep working.
 */

export const CALLBACK_PATH = '/auth/callback';

let configPromise = null;
let manager = null;
let leavingForSignOut = false;

// The login server this browser last signed in at, so that signing out never
// depends on the backend answering at that moment.
const LAST_CONFIG_KEY = 'tmr.signIn.server';
// When the person last chose to sign out (shared by every TMR tab).
const SIGNED_OUT_KEY = 'tmr.signedOutAt';
const SIGNED_OUT_WINDOW_MS = 90000;

const lastKnownServer = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(LAST_CONFIG_KEY) || 'null');
    return stored?.issuer && stored?.clientId ? stored : null;
  } catch (e) {
    return null;
  }
};

/**
 * Right after signing out, the login page waits for the person to ask
 * instead of opening the shared login by itself: that could sign them
 * straight back in (their Holedo session may still be alive in another tab
 * or service). Kept in localStorage so every tab of this browser sees it and
 * it survives the trip to the login server's sign-out and back.
 */
export const markSignedOut = () => localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()));
export const signedOutJustNow = () =>
  Date.now() - Number(localStorage.getItem(SIGNED_OUT_KEY) || 0) < SIGNED_OUT_WINDOW_MS;
export const clearSignedOut = () => localStorage.removeItem(SIGNED_OUT_KEY);

export const loadAuthConfig = () => {
  if (!configPromise) {
    const request = (cache) =>
      fetch(`${baseUrl}/rest/auth/config`, { headers: { Accept: 'application/json' }, cache });
    configPromise = request('default')
      // A copy the browser cached for another Holedo site is refused here as a
      // cross-origin error; ask the server itself once before giving up.
      .catch(() => request('reload'))
      .catch(() => {
        // The browser's own wording ("Failed to fetch") says nothing to a person.
        throw new Error('Holedo sign-in is unavailable. Please try again.');
      })
      .then((res) => {
        if (!res.ok) throw new Error('Holedo sign-in is unavailable. Please try again.');
        return res.json();
      })
      .then((body) => {
        const data = body?.data;
        if (!data || !['legacy', 'both', 'keycloak'].includes(data.provider)) {
          throw new Error('Holedo returned an invalid sign-in configuration.');
        }
        const enabled = ['both', 'keycloak'].includes(data.provider);
        if (enabled && !data.issuer) throw new Error('Holedo sign-in is not configured.');
        return {
          enabled,
          keycloakOnly: enabled && data.provider === 'keycloak',
          issuer: data.issuer || null,
          clientId: data.clients?.tmr || 'holedo-tmr',
          identityProviders: Array.isArray(data.identityProviders) ? data.identityProviders : [],
        };
      })
      .catch((error) => { configPromise = null; throw error; });
  }
  return configPromise;
};

const getManager = async ({ forSignOut = false } = {}) => {
  if (manager) return manager;
  const config = await loadAuthConfig().catch((error) => {
    // Signing out must still reach the login server when the backend does
    // not answer right now; otherwise the Holedo session would stay alive.
    const known = forSignOut ? lastKnownServer() : null;
    if (!known) throw error;
    return { enabled: true, ...known };
  });
  if (!config.enabled) return null;
  localStorage.setItem(LAST_CONFIG_KEY, JSON.stringify({ issuer: config.issuer, clientId: config.clientId }));

  manager = new UserManager({
    authority: config.issuer,
    client_id: config.clientId,
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
    post_logout_redirect_uri: `${window.location.origin}/login`,
    response_type: 'code',
    scope: 'openid profile email',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // The sign-in in progress is kept for the whole browser, not one tab:
    // when Keycloak sends an email in the middle of a sign-in (confirm your
    // address, connect an existing account), its link opens a new tab and
    // the return is completed there.
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    staleStateAgeInSeconds: PENDING_SIGN_IN_SECONDS,
    // Renewal is done below, one tab at a time.
    automaticSilentRenew: false,
    accessTokenExpiringNotificationTimeInSeconds: RENEW_BEFORE_SECONDS,
    monitorSession: false,
  });

  manager.events.addUserLoaded(applyToken);
  manager.events.addAccessTokenExpiring(() => renew(manager));
  manager.events.addAccessTokenExpired(() => renew(manager));

  // Another tab renewed, signed in or signed out.
  window.addEventListener('storage', (event) => {
    // key is null when another tab cleared the whole storage (sign-out).
    if (event.key !== null && !event.key.startsWith(USER_KEY_PREFIX)) return;
    if (event.key === null || event.newValue === null) {
      // Only a tab that was signed in follows. A tab on the login or return
      // page would start a new sign-in, and two such tabs keep sending each
      // other back when the account cannot open Recruitment.
      const wasSignedIn = storageService.hadSession();
      if (wasSignedIn && !localStorage.getItem('token')) window.location.assign('/login');
      return;
    }
    // Reads the stored session and restarts this tab's expiry timers.
    manager.getUser().then(applyToken).catch(() => {});
  });

  return manager;
};

const USER_KEY_PREFIX = 'oidc.user:';
// Longer than any step Keycloak lets a person take inside one sign-in.
const PENDING_SIGN_IN_SECONDS = 7200;
const RENEW_BEFORE_SECONDS = 60;
const RENEW_LOCK = 'holedo-keycloak-renew';
let retryTimer = null;

const applyToken = (user) => {
  // Keep TMR's API token current after each renewal.
  const email = localStorage.getItem('userEmail');
  const id = localStorage.getItem('userId');
  if (user?.access_token && email && id) {
    storageService.setToken(user.access_token, email, id);
  }
};

const endSession = () => {
  // Keycloak refused the refresh token: signed out elsewhere, revoked or expired.
  storageService.clearStorage();
  window.location.assign('/login');
};

const withLock = (work) =>
  navigator.locks?.request ? navigator.locks.request(RENEW_LOCK, work) : work();

/** Renew the access token, unless another tab already has. */
const renew = (um) =>
  withLock(async () => {
    const current = await um.getUser();
    if (!current) return null;
    if (current.expires_in > RENEW_BEFORE_SECONDS + 15) {
      // Another tab renewed while this one waited.
      applyToken(current);
      return current;
    }
    try {
      return await um.signinSilent();
    } catch (error) {
      const latest = await um.getUser();
      if (latest && latest.refresh_token !== current.refresh_token && !latest.expired) {
        // A tab without Web Locks support used the token first; take its result.
        applyToken(latest);
        return latest;
      }
      if (error?.name === 'ErrorResponse') {
        endSession();
        return null;
      }
      // Network trouble: keep the session and try again shortly.
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => renew(um), 30000);
      return current;
    }
  });

/**
 * Start sign-in on the shared Holedo page. `locale` ('en' | 'de') sets the
 * page's language. `replaceHistory` leaves no entry for the page that started
 * it, so Back from the Holedo page does not bounce straight forward again.
 */
export const signIn = async ({
  idpHint,
  register = false,
  returnPath = '/',
  locale,
  replaceHistory = false,
} = {}) => {
  // Sign-out is under way (see signOut): the page is about to unload.
  if (leavingForSignOut) return new Promise(() => {});
  clearSignedOut();
  const um = await getManager();
  if (!um) throw new Error('Holedo account sign-in is not available yet.');
  const extraQueryParams = {};
  if (idpHint) extraQueryParams.kc_idp_hint = idpHint;
  if (['en', 'de'].includes(locale)) extraQueryParams.ui_locales = locale;
  // Sign-ins that were started and never finished.
  await um.clearStaleState().catch(() => {});
  await um.signinRedirect({
    extraQueryParams,
    prompt: register ? 'create' : undefined,
    state: { returnPath: safeReturnPath(returnPath) },
    redirectMethod: replaceHistory ? 'replace' : 'assign',
  });
};

/** Finish sign-in on the callback page. Returns { token, returnPath }. */
export const completeSignIn = async () => {
  const um = await getManager();
  if (!um) throw new Error('Holedo account sign-in is not available.');
  const user = await um.signinRedirectCallback();
  return {
    token: user.access_token,
    idToken: user.id_token,
    returnPath: safeReturnPath(user.state?.returnPath),
  };
};

/**
 * End the Holedo sign-in at Keycloak after Recruitment could not open an
 * account for it. Without this the login page comes straight back with the
 * same account, and there is no way to choose another one.
 */
export const signOutAfterRefusal = async (idToken) => {
  const um = await getManager({ forSignOut: true });
  if (!um) return false;
  leavingForSignOut = true;
  setTimeout(() => {
    leavingForSignOut = false;
  }, 15000);
  storageService.clearStorage();
  markSignedOut();
  await um.signoutRedirect({ id_token_hint: idToken });
  return true;
};

/** Resume token renewal after a page load, in any tab that shares the session. */
export const resumeSession = async () => {
  const um = await getManager();
  if (!um) return false;
  const user = await um.getUser();
  if (!user) return false;
  if (user.expired || user.expires_in <= RENEW_BEFORE_SECONDS) {
    return !!(await renew(um));
  }
  applyToken(user);
  return true;
};

/**
 * Sign out of Holedo everywhere; returns false when there is no Keycloak
 * session. Clears TMR's stored data before leaving for Keycloak.
 */
export const signOut = async () => {
  const um = await getManager({ forSignOut: true });
  if (!um) return false;
  const user = await um.getUser();
  if (!user) return false;
  const idToken = user.id_token;
  // Until the browser has left for Keycloak's sign-out, the login page must
  // not start a new sign-in on top of it.
  leavingForSignOut = true;
  setTimeout(() => {
    leavingForSignOut = false;
  }, 15000);
  storageService.clearStorage();
  markSignedOut();
  await um.signoutRedirect({ id_token_hint: idToken });
  return true;
};

/**
 * Sign out, from any menu. With a Holedo sign-in session this leaves for
 * Keycloak (which ends the session in every Holedo service) and returns
 * true; otherwise it ends the old-style session here and returns false, and
 * the caller shows the login page.
 */
export const signOutEverywhere = async () => {
  const token = storageService.getToken();
  const redirected = await signOut().catch(() => false);
  if (redirected) return true;
  if (token) {
    // End the session on the server, so a copied token stops working.
    fetch(`${baseUrl}/rest/users/logout`, {
      method: 'POST',
      headers: { Accept: 'application/json', AuthAPI: `Bearer ${token}` },
    }).catch(() => {});
  }
  storageService.clearStorage();
  markSignedOut();
  return false;
};

/** Drop the stored Keycloak session without contacting Keycloak. */
export const forgetSession = async () => {
  const um = await getManager().catch(() => null);
  if (um) await um.removeUser().catch(() => {});
};

const safeReturnPath = (path) =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\')
    ? path
    : '/';
